import { EDITED_PATIENT_NAME_EXTENSION } from '@bariendo/lib/extensions';
import { formatHumanName } from '@medplum/core';
import { HumanName, Patient } from '@medplum/fhirtypes';

/**
 * How Orbit edits a patient's names. The header's ⋯ "Edit name" and the PN
 * consultation card's "Confirm their details" both save through
 * `savePatientProfileEdit`, which builds the new list here.
 *
 * - **Legal name** is the `official` HumanName, or a name with no `use` —
 *   bariendo-be writes signup names without one. It is tagged
 *   `EDITED_PATIENT_NAME_EXTENSION`, which tells the Healthie sync to push it
 *   rather than overwrite it.
 * - **Preferred name** is the `usual` HumanName, appended after the others.
 *   The card refuses one for a patient with no legal name, so it is never the
 *   only name and never `name[0]`. Removing it drops the entry.
 *
 * Order matters: Customer.io, PostHog, Healthie and ~130 Orbit call sites read
 * `name[0]` as the patient's name, so a new legal name goes first and a
 * preferred name never does. Every other name entry, and every field of the
 * entry being edited, is kept.
 */

type PatientNameEdit =
  | { kind: 'legal'; given: string; family: string }
  | { kind: 'preferred'; name: string }
  | { kind: 'removePreferred' };

function legalNameIndex(names: HumanName[]): number {
  const official = names.findIndex((name) => name.use === 'official');
  return official !== -1 ? official : names.findIndex((name) => name.use === undefined);
}

function preferredNameIndex(names: HumanName[]): number {
  return names.findIndex((name) => name.use === 'usual');
}

/** The legal name as editable text: all given names joined, so a middle name is shown rather than dropped. */
export function readLegalName(patient: Patient | undefined): { given: string; family: string } {
  const names = patient?.name ?? [];
  const legal = names[legalNameIndex(names)];
  return { given: legal?.given?.join(' ') ?? '', family: legal?.family ?? '' };
}

export function readPreferredName(patient: Patient | undefined): string {
  const names = patient?.name ?? [];
  const preferred = names[preferredNameIndex(names)];
  return preferred?.given?.join(' ') || preferred?.text || '';
}

function withEditedTag(name: HumanName | undefined): HumanName['extension'] {
  return [
    ...(name?.extension?.filter((ext) => ext.url !== EDITED_PATIENT_NAME_EXTENSION) ?? []),
    { url: EDITED_PATIENT_NAME_EXTENSION, valueBoolean: true },
  ];
}

/**
 * The editor shows every given name joined, so an untouched first-and-middle
 * field keeps the stored array, middle names included. An edited one is stored
 * as typed, one entry, as the header always has: splitting it would send only
 * the first word of "Mary Ann" to Healthie as the first name.
 */
function givenNames(text: string, existing: HumanName | undefined): string[] | undefined {
  if (existing?.given && text === existing.given.join(' ')) return existing.given;
  return text ? [text] : undefined;
}

/** The Patient's name list with one edit applied. */
export function withPatientName(names: HumanName[] | undefined, edit: PatientNameEdit): HumanName[] {
  const list = names ?? [];
  // Every `usual` entry, so no second one surfaces as the preferred name once the first is gone.
  if (edit.kind === 'removePreferred') return list.filter((name) => name.use !== 'usual');
  const index = edit.kind === 'legal' ? legalNameIndex(list) : preferredNameIndex(list);
  const existing = index === -1 ? undefined : list[index];
  const value: HumanName =
    edit.kind === 'legal'
      ? {
          use: 'official',
          ...existing,
          given: givenNames(edit.given, existing),
          family: edit.family || undefined,
          extension: withEditedTag(existing),
        }
      : { use: 'usual', ...existing, given: [edit.name] };

  if (existing) {
    return list.map((name, i) => (i === index ? value : name));
  }
  return edit.kind === 'legal' ? [value, ...list] : [...list, value];
}

/**
 * The patient header's name line, split around the preferred name so it reads
 * `Jane “Janie” Marie Doe` (BAR-2993). The legal name is `name[0]`, as every
 * other reader takes it; the preferred name goes right after its first given
 * name, and only when it differs from that first name. `preferred` is absent
 * when there is nothing to add, and `before` is then the whole name.
 */
export function headerNameParts(patient: Patient | undefined): { before: string; preferred?: string; after: string } {
  const legal = patient?.name?.[0];
  const whole = { before: formatHumanName(legal), after: '' };
  const preferred = readPreferredName(patient).trim();
  if (!legal || !preferred || (!legal.given?.length && !legal.family)) return whole;

  const [first, ...rest] = legal.given ?? [];
  if (preferred.toLowerCase() === (first ?? '').toLowerCase()) return whole;
  return {
    before: formatHumanName({ prefix: legal.prefix, given: first ? [first] : undefined }),
    preferred,
    after: formatHumanName({ given: rest, family: legal.family, suffix: legal.suffix }),
  };
}
